// Chuyển khách thẻ liệu trình sang Tư vấn cơ sở: tình trạng các thẻ thành nhu cầu tư vấn, khách vào hàng chờ và được chia tự động
import { useTuVan } from '../AppTuVan/store';
import { allCodes, type Visit } from '../AppTuVan/logic';
import { activeVisitOf, visitFromCard, type CardReferral } from '../AppTuVan/tele';
import type { TreatmentCard, TreatmentConfig } from './data';
import { cardBalance, cardFlags, expiryText } from './logic';
import { CARD_STATUS } from './ui';

const fmt = (n: number) => new Intl.NumberFormat('vi-VN').format(Math.round(n));

/** Lý do nên tư vấn, theo thứ tự ưu tiên */
const REASONS: [(f: ReturnType<typeof cardFlags>) => boolean, string][] = [
  [(f) => f.status === 'completed', 'đã dùng hết'],
  [(f) => f.status === 'expired', 'đã hết hạn'],
  [(f) => f.status === 'active' && f.low, 'sắp hết buổi'],
  [(f) => f.status === 'active' && f.expiring, 'sắp hết hạn'],
];

/** Thẻ có lý do để mời tư vấn (hết buổi, hết hạn, sắp hết) */
export const needsConsult = (cards: TreatmentCard[], cfg: TreatmentConfig) =>
  cards.some((c) => { const f = cardFlags(c, cfg); return REASONS.some(([hit]) => hit(f)); });

export function cardReferral(cust: { id: string; name: string; phone: string }, cards: TreatmentCard[], cfg: TreatmentConfig): CardReferral {
  const rows = cards.map((c) => {
    const f = cardFlags(c, cfg);
    const b = cardBalance(c);
    const left = c.kind === 'value' ? `còn ${fmt(b.remainingValue)}đ` : `còn ${b.remainingSessions}/${b.totalSessions} buổi`;
    const notes = [
      CARD_STATUS[f.status].label,
      f.status === 'active' && f.low ? 'sắp hết buổi' : '',
      f.status === 'active' && f.expiring ? `hết hạn sau ${f.daysToExpire} ngày` : '',
      f.debt > 0 ? `còn nợ ${fmt(f.debt)}đ` : '',
    ].filter(Boolean);
    return { c, f, label: `${c.packageName} · ${c.id}`, value: `${left} · hạn ${expiryText(c)} · ${notes.join(', ')}` };
  });
  const hit = REASONS.map(([test, why]) => ({ row: rows.find((r) => test(r.f)), why })).find((x) => x.row);
  return {
    name: cust.name,
    phone: cust.phone,
    // Mã thẻ dễ tra hơn mã khách nội bộ của POS
    code: cards[0]?.id ?? cust.id,
    need: hit?.row ? `Thẻ ${hit.row.c.packageName} ${hit.why} — tư vấn gói tiếp theo` : 'Khách thẻ liệu trình muốn tư vấn thêm dịch vụ',
    cards: rows.map(({ label, value }) => ({ label, value })),
    hint: rows.map((r) => `${r.c.packageName} ${r.c.category}`).join(' '),
  };
}

/** Khách đang chờ / đang tư vấn hôm nay ở Tư vấn cơ sở (null nếu chưa chuyển) */
export const consultVisitOf = (visits: Visit[], phone: string) => activeVisitOf(visits, phone, Date.now()) ?? null;

/** Đưa khách vào hàng chờ Tư vấn cơ sở; đã có lượt đang chờ / đang tư vấn thì không tạo trùng */
export function referToConsult(r: CardReferral, by: string): { visit: Visit; created: boolean } {
  const st = useTuVan.getState();
  const now = Date.now();
  const had = activeVisitOf(st.visits, r.phone, now);
  if (had) return { visit: had, created: false };
  const visit = visitFromCard(r, st.branchId, allCodes(st.visits), by, now);
  st.add([visit]);
  return { visit, created: true };
}
